import { useEffect, useState, useMemo } from "react";
import DateInput from '../../components/DateInput';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../config";
import { showSuccess } from "../../utils/dialog";
import { SignaturePanel } from "../../shared/SignaturePanel";
import "../../Styles/AccessibilityStndCustmer.css";
import talentLogo from "../../assets/talentlogo2.png";

const HolidayAck = () => {
  const navigate = useNavigate();
  const itemId = "holiday-ack";
  const fieldOrder = useMemo(() => ["agreement", "date", "employeeName", "employeeSignature"], []);
  const [formData, setFormData] = useState(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    const initial = saved ? JSON.parse(saved) : {};
    return fieldOrder.reduce((acc, f) => {
      acc[f] = (initial[f] ?? "");
      return acc;
    }, {});
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [err, setErr] = useState("");

  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    if (saved) {
      const raw = JSON.parse(saved);
      const normalized = fieldOrder.reduce((acc, f) => {
        acc[f] = (raw[f] ?? "");
        return acc;
      }, {});
      setFormData(normalized);
    }
  }, [fieldOrder]);

  // const isDateToday = (val) => {
  //   if (!val) return false;
  //   const input = new Date(val);
  //   const now = new Date();
  //   const y = now.getFullYear();
  //   const m = now.getMonth();
  //   const d = now.getDate();
  //   return (
  //     input.getFullYear() === y && input.getMonth() === m && input.getDate() === d
  //   );
  // };

  const isDateToday = (val) => {
    if (!val) return false;
    
    // Get current date in User's Local Time
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(now.getDate()).padStart(2, '0');

    // Create a standardized string "YYYY-MM-DD" based on local time
    const todayString = `${year}-${month}-${day}`;

    // Compare the strings directly
    return val === todayString;
  };

  // Auto-save draft. Never set "complete" here (only on Submit). If already complete, preserve it when user reopens and goes back.
  useEffect(() => {
    const values = fieldOrder.map((f) => (formData[f] ?? "").toString().trim());
    const allEmpty = values.every((v) => v.length === 0);
    const accountId = localStorage.getItem("accountId");
    const existing = (accountId ? localStorage.getItem(`status_${accountId}_${itemId}`) : null) || localStorage.getItem(`status_${itemId}`);
    const statusToPersist = allEmpty ? "incomplete" : (existing === "complete" ? "complete" : "in-progress");
    localStorage.setItem(`status_${accountId}_${itemId}`, statusToPersist);
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));
    if (accountId && statusToPersist !== "incomplete") {
      axios.post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, {
        status: statusToPersist,
        formData: {}
      }).catch(err => console.error("Failed to sync status:", err));
    }
  }, [formData, fieldOrder]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setErr("");
    setFieldErrors({});

    const errors = {};

    if (!formData.agreement) {
      errors.agreement = "Please choose one option";
    }
    if (!formData.date) {
      errors.date = "This field is required";
    } else if (!isDateToday(formData.date)) {
      errors.date = "Date must be today";
    }
    if (!String(formData.employeeName || "").trim()) {
      errors.employeeName = "This field is required";
    }
    if (!formData.employeeSignature) {
      errors.employeeSignature = "Signature is required";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setErr("Please fill the required fields!");
      return;
    }

    const accountId = localStorage.getItem("accountId");
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));
    const values = fieldOrder.map((f) => (formData[f] ?? "").toString().trim());
    const allEmpty = values.every((v) => v.length === 0);
    const allFilled = values.every((v) => v.length !== 0);
    const dateValid = isDateToday(formData.date);
    const agreementValid = ["accept", "decline"].includes(String(formData.agreement || ""));
    const signatureValid = !!(formData.employeeSignature && String(formData.employeeSignature).length > 0);
    const canBeComplete = allFilled && dateValid && agreementValid && signatureValid;
    const newStatus = allEmpty ? "incomplete" : canBeComplete ? "complete" : "in-progress";
    const payload = { status: newStatus, formData };
    if (accountId && newStatus === 'complete') {
      axios.post(`${BASE_URL}/api/registration/${accountId}/items/${itemId}/complete`, payload)
        .catch(err => console.error('Failed to save holiday ack:', err))
        .finally(() => {
          axios.post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, { status: newStatus, formData: {} }).catch(() => {});
          showSuccess("Form completed successfully!");
          navigate('/register');
        });
    } else {
      navigate('/register');
    }
  };

  const handleBack = () => {
    navigate("/register");
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear field-level errors when corrected
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      if (field === "date") {
        if (value && isDateToday(value)) delete next[field];
      } else if (field === "agreement") {
        if (["accept", "decline"].includes(String(value))) delete next[field];
      } else {
        if (String(value || "").trim()) delete next[field];
      }
      return next;
    });
  };

  return (
    <div className="accessibility-form-page">
      <div className="form-container">
        <form className="form-card" onSubmit={handleSubmit}>
          <header className="form-card-header" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <img src={talentLogo} alt="Talent Employment Inc." style={{ height: 64, marginBottom: 10 }} />
              <h2 className="form-title" style={{ textAlign: "center" }}>
                Statutory Holiday Acknowledgement
              </h2>
          </header>

          <section className="form-card-content">
            {err && <div className="error-text" style={{ color: "#dc2626", marginBottom: 12 }}>{err}</div>}
            <p className="form-intro">
              Dear Talent Employment Inc. Contract Employee:
            </p>
            <p className="form-intro paragraph-space">
              This letter is to inform you that in the scenario where a Talent Employment Inc. Client is in full operation during a statutory holiday, you may be scheduled to report to work. Per Provincial Employment Standards, if you are asked to work a statutory holiday, you may be eligible to receive premium pay at the rate of 1.5 times your hourly pay rate or a substitute day off.
            </p>
            <p className="form-intro paragraph-space">
                If you have any questions regarding a statutory holiday or pay eligibility, please contact your Talent Employment Inc. representative.
            </p>
            <p className="form-intro paragraph-space">
              Thank you,
            </p>
            <p className="form-intro paragraph-space">
              Talent Employment Inc. Management
            </p>
            <p style={{ fontSize: "12px" }}>Please accept or decline*</p>
            <label className="option">
              <input
                type="radio"
                name="agreement"
                value="accept"
                checked={formData.agreement === "accept"}
                onChange={(e) => handleInputChange("agreement", e.target.value)}
              />
              <span>I hereby ACCEPT the terms of this Acknowledgement and agree to work statutory holidays when scheduled.</span>
            </label>
            <label className="option">
              <input
                type="radio"
                name="agreement"
                value="decline"
                checked={formData.agreement === "decline"}
                onChange={(e) => handleInputChange("agreement", e.target.value)}
              />
              <span>I hereby DECLINE the terms of this Acknowledgement; I do not want to work any of the statutory holidays.</span>
            </label>
            {fieldErrors.agreement && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.agreement}</div>}
          </section>

          <div className="grid-2">
            <div className="form-field">
              <label htmlFor="date">Date*</label>
              <DateInput
                id="date"
                value={formData.date}
                onChange={(v) => handleInputChange("date", v)}
                required
              />
              {fieldErrors.date && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.date}</div>}
            </div>

            <div className="form-field">
              <label htmlFor="employeeName">Employee's Full Name*</label>
              <input
                id="employeeName"
                type="text"
                value={formData.employeeName}
                onChange={(e) => handleInputChange("employeeName", e.target.value)}
                required
              />
              {fieldErrors.employeeName && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.employeeName}</div>}
            </div>
          </div>

          <div className="signature-panel-wrapper">
            <SignaturePanel
              onSignatureChange={(dataURL) =>
                handleInputChange("employeeSignature", dataURL)
              }
              initialSignature={formData.employeeSignature}
              required
            />
            {fieldErrors.employeeSignature && <div className="error-text" style={{ color: "#dc2626", marginTop: 6 }}>{fieldErrors.employeeSignature}</div>}
          </div>

          <div className="form-buttons">
            <button type="button" onClick={handleBack}>Back</button>
            <button type="submit" onClick={handleSubmit}>Submit</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HolidayAck;
